/**
 * media-upload.js
 * Manages media file selection (image / video / audio) for the Media tab.
 *
 * Files are kept in memory (window.mediaUploadFiles[]) and are NEVER sent
 * to the server before payment. Payment handlers can read the array after
 * checkout to perform the actual upload.
 */

(function () {
    'use strict';

    // ── Constants ────────────────────────────────────────────────────────────

    const MAX_BYTES = 10 * 1024 * 1024; // 10 MB

    /** Visual identity per MIME prefix */
    const TYPE_MAP = {
        'image/': {
            icon: 'bi-image',
            label: 'Image',
            fg: '#2563eb',
            thumbBg: '#dbeafe',
            pillBg: '#eff6ff',
        },
        'video/': {
            icon: 'bi-play-circle-fill',
            label: 'Video',
            fg: '#7c3aed',
            thumbBg: '#ede9fe',
            pillBg: '#f5f3ff',
        },
        'audio/': {
            icon: 'bi-music-note-beamed',
            label: 'Audio',
            fg: '#db2777',
            thumbBg: '#fce7f3',
            pillBg: '#fdf4f8',
        },
    };

    // ── Shared file store (accessible by payment handlers) ───────────────────
    window.mediaUploadFiles = window.mediaUploadFiles || [];

    // ── Server-file store: files already saved on the server ─────────────────
    // Array of { uid, url, name, mediaType } — populated from storedInvitationData on init
    // uid matches data-theme-uid on field inputs, so deletions flow through existing logic.
    window.mediaServerFiles = window.mediaServerFiles || [];

    // Detect MIME-like category from a URL/filename extension
    function typeInfoFromUrl(url) {
        const ext = (url.split('?')[0].split('.').pop() || '').toLowerCase();
        const imageExts = ['jpg','jpeg','png','gif','webp','bmp','svg','avif'];
        const videoExts = ['mp4','webm','ogg','mov','avi','mkv','m4v'];
        const audioExts = ['mp3','wav','ogg','aac','flac','m4a','opus'];
        if (imageExts.includes(ext)) return { prefix: 'image/', ...TYPE_MAP['image/'] };
        if (videoExts.includes(ext)) return { prefix: 'video/', ...TYPE_MAP['video/'] };
        if (audioExts.includes(ext)) return { prefix: 'audio/', ...TYPE_MAP['audio/'] };
        return { prefix: 'image/', ...TYPE_MAP['image/'] }; // fallback
    }

    // Load server files from storedInvitationData (update-invite only)
    function loadServerFiles() {
        var sd = window.storedInvitationData;
        if (!sd || !sd.success || !sd.data || !sd.data.files) return;
        var files = sd.data.files;
        window.mediaServerFiles = [];
        Object.keys(files).forEach(function (uid) {
            var url = files[uid];
            if (!url || typeof url !== 'string') return;
            var info = typeInfoFromUrl(url);
            var name = url.split('/').pop().split('?')[0] || uid;
            window.mediaServerFiles.push({
                uid: uid,
                url: url,
                name: name,
                mediaType: info.prefix.replace('/', ''), // 'image', 'video', 'audio'
                info: info,
            });
        });
    }

    // Mark a server file as deleted (sets data-file-action on the matching input)
    function deleteServerFile(uid) {
        window.mediaServerFiles = window.mediaServerFiles.filter(function (f) { return f.uid !== uid; });
        // Tell getInvitationFiles() to send status=delete for this uid
        var input = document.querySelector('[data-theme-uid="' + uid + '"]');
        if (input) {
            input.setAttribute('data-file-action', 'delete');
            input._selectedFiles = null;
            input.value = '';
            // Also reset the preview card so it shows empty/default state
            var card = input.closest('.media-preview-card');
            if (card) {
                var previewContainer = card.querySelector('.preview-container');
                var defaultSrc = input.getAttribute('data-media-default') || '';
                var mediaType  = input.getAttribute('data-media-type') || 'image';
                var themeUid   = uid;
                if (previewContainer) {
                    if (defaultSrc) {
                        if (mediaType === 'video') {
                            previewContainer.innerHTML =
                                '<div class="preview-content video-preview">' +
                                    '<video src="' + defaultSrc + '" data-key-uid="' + themeUid + '" class="preview-video" muted loop playsinline preload="metadata"></video>' +
                                '</div>';
                        } else {
                            previewContainer.innerHTML =
                                '<div class="preview-content image-preview">' +
                                    '<img src="' + defaultSrc + '" alt="Preview" data-key-uid="' + themeUid + '" class="preview-image">' +
                                '</div>';
                        }
                    } else {
                        previewContainer.innerHTML =
                            '<div class="preview-content empty-state">' +
                                (mediaType === 'video'
                                    ? '<i class="bi bi-camera-video"></i><span>Click to upload video</span>'
                                    : mediaType === 'audio'
                                        ? '<i class="bi bi-music-note-beamed"></i><span>Click to upload audio</span>'
                                        : '<i class="bi bi-image"></i><span>Click to upload image</span>') +
                            '</div>';
                    }
                }
                var delBtn = card.querySelector('.media-delete-button');
                if (delBtn) delBtn.setAttribute('data-show-btn', 'false');
                var muteBtn = card.querySelector('.media-mute-button');
                if (muteBtn) muteBtn.setAttribute('data-show-btn', defaultSrc ? 'true' : 'false');
            }
            // Also reset background music if this uid is background_music
            if (uid === 'background_music' && typeof window.removeMusicFile === 'function') {
                window.removeMusicFile();
            }
        }
        renderList();
    }

    // ── State ─────────────────────────────────────────────────────────────────
    let initialized = false;
    let ppmFilesInitialized = false;
    let dropzone, fileInput, fileList, headerCount;
    // Object-URL cache so we can revoke on removal to free memory
    const objectURLCache = new Map(); // file ref → object URL

    // ── Helpers ───────────────────────────────────────────────────────────────

    function typeInfo(file) {
        for (const [prefix, info] of Object.entries(TYPE_MAP)) {
            if (file.type.startsWith(prefix)) return info;
        }
        return null;
    }

    function fmtSize(bytes) {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    }

    function esc(str) {
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    // ── Random 16-char filename generator ────────────────────────────────────
    const CHARS = 'abcdefghijklmnopqrstuvwxyz0123456789';

    function randomName(file) {
        // Preserve the original extension so the server knows the format
        const dotIdx = file.name.lastIndexOf('.');
        const ext = dotIdx !== -1 ? file.name.slice(dotIdx) : '';
        let id = '';
        for (let i = 0; i < 16; i++) {
            id += CHARS[Math.floor(Math.random() * CHARS.length)];
        }
        return id + ext;
    }

    // ── File management ───────────────────────────────────────────────────────

    function addFiles(fileArray) {
        const errors = [];
        Array.from(fileArray).forEach(function (file) {
            const info = typeInfo(file);
            if (!info) {
                errors.push('"' + file.name + '" — unsupported file type.');
                return;
            }
            if (file.size > MAX_BYTES) {
                errors.push('"' + file.name + '" exceeds the 10 MB limit (' + fmtSize(file.size) + ').');
                return;
            }
            // Deduplicate by ORIGINAL name + size (before rename)
            const exists = window.mediaUploadFiles.some(function (f) {
                return f._originalName === file.name && f._originalSize === file.size;
            });
            if (!exists) {
                // Rename to a 16-char random name, keep type and content
                const renamed = new File([file], randomName(file), { type: file.type });
                renamed._originalName = file.name;
                renamed._originalSize = file.size;
                window.mediaUploadFiles.push(renamed);
            }
        });

        renderList();
        if (errors.length) showToast(errors);
    }

    function removeFile(idx) {
        const file = window.mediaUploadFiles[idx];
        if (file) {
            // Free the cached object URL if any
            const url = objectURLCache.get(file);
            if (url) { URL.revokeObjectURL(url); objectURLCache.delete(file); }
        }
        window.mediaUploadFiles.splice(idx, 1);
        renderList();
    }

    // ── Rendering ─────────────────────────────────────────────────────────────

    function renderListTo(target, hc) {
        if (!target) return;
        target.innerHTML = '';

        var serverCount = window.mediaServerFiles.length;
        var newCount    = window.mediaUploadFiles.length;
        var totalCount  = serverCount + newCount;

        if (totalCount === 0) {
            target.style.display = 'none';
            if (hc) hc.style.display = 'none';
            return;
        }

        target.style.display = '';
        if (hc) {
            hc.textContent = totalCount;
            hc.style.display = '';
        }

        // Build a 3-column grid wrapper
        var grid = document.createElement('div');
        grid.className = 'mup-grid';

        // ── Section label helper ─────────────────────────────────────────────
        function sectionLabel(text) {
            var lbl = document.createElement('div');
            lbl.className = 'mup-section-label';
            lbl.textContent = text;
            return lbl;
        }

        // ── Server (saved) files ─────────────────────────────────────────────
        if (serverCount > 0) {
            if (newCount > 0) grid.appendChild(sectionLabel('Saved on server'));
            window.mediaServerFiles.forEach(function (sf) {
                var info = sf.info || { icon: 'bi-file-earmark', label: 'File', fg: '#888', thumbBg: '#f0f0f0', pillBg: '#f8f8f8' };
                var mt = sf.mediaType || 'image';

                var previewHTML;
                if (mt === 'image') {
                    previewHTML = '<img class="mup-card-img" src="' + esc(sf.url) + '" alt="">';
                } else if (mt === 'video') {
                    previewHTML =
                        '<video class="mup-card-video" src="' + esc(sf.url) + '" muted playsinline preload="metadata"></video>' +
                        '<span class="mup-card-play-icon"><i class="bi bi-play-circle-fill"></i></span>';
                } else {
                    previewHTML =
                        '<span class="mup-card-icon-fallback" style="color:' + info.fg + '">' +
                            '<i class="bi ' + info.icon + '"></i>' +
                        '</span>';
                }

                var card = document.createElement('div');
                card.className = 'mup-card mup-card--server';
                card.innerHTML =
                    '<div class="mup-card-visual" style="background:' + info.thumbBg + '">' +
                        previewHTML +
                        '<span class="mup-card-badge mup-card-badge--saved" style="background:#dcfce7;color:#166534">Saved</span>' +
                        '<button type="button" class="mup-card-remove" data-server-uid="' + esc(sf.uid) + '" title="Remove">' +
                            '<i class="bi bi-x"></i>' +
                        '</button>' +
                    '</div>';

                var uid = sf.uid;
                card.querySelector('.mup-card-remove').addEventListener('click', function (e) {
                    e.stopPropagation();
                    deleteServerFile(uid);
                });

                card.addEventListener('click', function (e) {
                    if (e.target.closest('.mup-card-remove')) return;
                    openLightboxUrl(sf);
                });

                grid.appendChild(card);
            });
        }

        // ── New (local) files ────────────────────────────────────────────────
        if (newCount > 0) {
            if (serverCount > 0) grid.appendChild(sectionLabel('New — pending upload'));
            window.mediaUploadFiles.forEach(function (file, i) {
                var info = typeInfo(file) || { icon: 'bi-file-earmark', label: 'File', fg: '#888', thumbBg: '#f0f0f0', pillBg: '#f8f8f8' };
                var isImage = file.type.startsWith('image/');
                var isVideo = file.type.startsWith('video/');

                var previewHTML;
                if (isImage) {
                    var src = objectURLCache.get(file);
                    if (!src) { src = URL.createObjectURL(file); objectURLCache.set(file, src); }
                    previewHTML = '<img class="mup-card-img" src="' + src + '" alt="">';
                } else if (isVideo) {
                    var src = objectURLCache.get(file);
                    if (!src) { src = URL.createObjectURL(file); objectURLCache.set(file, src); }
                    previewHTML =
                        '<video class="mup-card-video" src="' + src + '" muted playsinline preload="metadata"></video>' +
                        '<span class="mup-card-play-icon"><i class="bi bi-play-circle-fill"></i></span>';
                } else {
                    previewHTML =
                        '<span class="mup-card-icon-fallback" style="color:' + info.fg + '">' +
                            '<i class="bi ' + info.icon + '"></i>' +
                        '</span>';
                }

                var card = document.createElement('div');
                card.className = 'mup-card';
                card.innerHTML =
                    '<div class="mup-card-visual" style="background:' + info.thumbBg + '">' +
                        previewHTML +
                        '<span class="mup-card-badge" style="background:' + info.pillBg + ';color:' + info.fg + '">' +
                            info.label +
                        '</span>' +
                        '<button type="button" class="mup-card-remove" data-idx="' + i + '" title="Remove">' +
                            '<i class="bi bi-x"></i>' +
                        '</button>' +
                    '</div>';

                grid.appendChild(card);
            });
        }

        target.appendChild(grid);

        // Bind remove buttons for new files
        grid.querySelectorAll('.mup-card-remove[data-idx]').forEach(function (btn) {
            btn.addEventListener('click', function (e) {
                e.stopPropagation();
                removeFile(parseInt(btn.dataset.idx, 10));
            });
        });

        // Bind card clicks → lightbox for new files (by index in mediaUploadFiles)
        // Server file cards already have their own click listeners above
        var newCards = Array.from(grid.querySelectorAll('.mup-card:not(.mup-card--server)'));
        newCards.forEach(function (card, i) {
            card.addEventListener('click', function (e) {
                if (e.target.closest('.mup-card-remove')) return;
                openLightbox(window.mediaUploadFiles[i]);
            });
        });
    }

    function renderList() {
        renderListTo(fileList, headerCount);
        // Also mirror to the PPM files panel when it is currently visible
        var ppmEl = document.getElementById('ppmMupFileList');
        if (ppmEl) {
            var ppmBox = ppmEl.closest('[data-ppm-tab="ppm-files"]');
            if (ppmBox && ppmBox.dataset.ppmState === 'show') {
                renderListTo(ppmEl, null);
            }
        }
    }

    // ── Lightbox for server-file (URL-only, no File object) ──────────────────
    function openLightboxUrl(sf) {
        initLightbox();
        var lb      = document.getElementById('mupLightbox');
        var content = document.getElementById('mupLightboxContent');
        if (!lb || !content) return;
        content.innerHTML = '';
        var mt = sf.mediaType || 'image';
        if (mt === 'image') {
            var img = document.createElement('img');
            img.src = sf.url;
            img.className = 'mup-lb-img';
            content.appendChild(img);
        } else if (mt === 'video') {
            var vid = document.createElement('video');
            vid.src = sf.url;
            vid.className = 'mup-lb-video';
            vid.controls = true;
            vid.autoplay = true;
            vid.playsInline = true;
            content.appendChild(vid);
        } else {
            var info = sf.info || { fg: '#db2777' };
            var wrapper = document.createElement('div');
            wrapper.className = 'mup-lb-audio-wrapper';
            wrapper.innerHTML =
                '<div class="mup-lb-audio-icon" style="color:' + info.fg + '">' +
                    '<i class="bi bi-music-note-beamed"></i>' +
                '</div>' +
                '<p class="mup-lb-audio-name">' + esc(sf.name) + '</p>';
            var audio = document.createElement('audio');
            audio.src = sf.url;
            audio.controls = true;
            audio.autoplay = true;
            audio.className = 'mup-lb-audio';
            wrapper.appendChild(audio);
            content.appendChild(wrapper);
        }
        lb.classList.add('mup-lightbox--open');
    }

    // ── Lightbox ────────────────────────────────────────────────────────────

    let lbInitialized = false;

    function initLightbox() {
        if (lbInitialized) return;
        const lb       = document.getElementById('mupLightbox');
        const backdrop = document.getElementById('mupLightboxBackdrop');
        const closeBtn = document.getElementById('mupLightboxClose');
        if (!lb) return;
        lbInitialized = true;

        function close() {
            lb.classList.remove('mup-lightbox--open');
            // Pause any playing media
            const content = document.getElementById('mupLightboxContent');
            if (content) {
                content.querySelectorAll('video, audio').forEach(function (m) { m.pause(); });
                setTimeout(function () { content.innerHTML = ''; }, 300);
            }
        }

        closeBtn.addEventListener('click', close);
        backdrop.addEventListener('click', close);

        document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape') close();
        });
    }

    function openLightbox(file) {
        initLightbox();
        const lb      = document.getElementById('mupLightbox');
        const content = document.getElementById('mupLightboxContent');
        if (!lb || !content) return;

        content.innerHTML = '';

        let src = objectURLCache.get(file);
        if (!src) { src = URL.createObjectURL(file); objectURLCache.set(file, src); }

        if (file.type.startsWith('image/')) {
            const img = document.createElement('img');
            img.src = src;
            img.className = 'mup-lb-img';
            img.alt = '';
            content.appendChild(img);

        } else if (file.type.startsWith('video/')) {
            const vid = document.createElement('video');
            vid.src = src;
            vid.className = 'mup-lb-video';
            vid.controls = true;
            vid.autoplay = true;
            vid.playsInline = true;
            content.appendChild(vid);

        } else if (file.type.startsWith('audio/')) {
            const info = typeInfo(file);
            const wrapper = document.createElement('div');
            wrapper.className = 'mup-lb-audio-wrapper';
            wrapper.innerHTML =
                '<div class="mup-lb-audio-icon" style="color:' + (info ? info.fg : '#db2777') + '">' +
                    '<i class="bi bi-music-note-beamed"></i>' +
                '</div>' +
                '<p class="mup-lb-audio-name">' + esc(file._originalName || file.name) + '</p>';
            const audio = document.createElement('audio');
            audio.src = src;
            audio.controls = true;
            audio.autoplay = true;
            audio.className = 'mup-lb-audio';
            wrapper.appendChild(audio);
            content.appendChild(wrapper);
        }

        lb.classList.add('mup-lightbox--open');
    }

    // ── Toast notification ────────────────────────────────────────────────────

    function showToast(messages) {
        const existing = document.getElementById('mupErrorToast');
        if (existing) existing.remove();

        const toast = document.createElement('div');
        toast.id = 'mupErrorToast';
        toast.className = 'mup-error-toast';
        toast.innerHTML = messages.map(function (m) {
            return '<div><i class="bi bi-exclamation-circle"></i> ' + esc(m) + '</div>';
        }).join('');

        document.body.appendChild(toast);
        // Trigger CSS transition
        requestAnimationFrame(function () {
            requestAnimationFrame(function () { toast.classList.add('mup-error-toast--show'); });
        });
        setTimeout(function () {
            toast.classList.remove('mup-error-toast--show');
            setTimeout(function () { toast.remove(); }, 300);
        }, 4000);
    }

    // ── Apply a file to a media input + preview ────────────────────────────────

    function resolveTargets(triggerEl) {
        // triggerEl could be the .media-file-input OR the .preview-container (same id bug)
        var card = triggerEl.closest('.media-preview-card');

        // Input may be a SIBLING of .media-preview-card, not inside it
        // (e.g. cover-image / branding-logo templates: .form-group > .media-preview-card + input)
        if (!card && triggerEl.parentElement) {
            card = triggerEl.parentElement.querySelector('.media-preview-card');
        }

        var inputEl = null;
        var previewEl = null;

        if (card) {
            previewEl = card.querySelector('.preview-container');
            // When triggerEl is the input itself (sibling case), keep it as inputEl;
            // otherwise look for an input inside the card.
            if (triggerEl.classList.contains('media-file-input')) {
                inputEl = triggerEl;
            } else {
                inputEl = card.querySelector('.media-file-input');
                // input might be a sibling of the card — check parent
                if (!inputEl && card.parentElement) {
                    inputEl = card.parentElement.querySelector('.media-file-input');
                }
            }
        }
        // Fallback: triggerEl itself is the input
        if (!inputEl && triggerEl.classList.contains('media-file-input')) {
            inputEl = triggerEl;
        }
        return { inputEl: inputEl, previewEl: previewEl };
    }

    function applyFileToInput(inputEl, previewEl, filesArr) {
        if (!inputEl || !filesArr.length) return;
        var file = filesArr[0];
        var mediaType = (inputEl.getAttribute('data-media-type') || 'image').toLowerCase();
        var uid = inputEl.getAttribute('data-theme-uid') || '';

        // Object URL
        var src = objectURLCache.get(file);
        if (!src) { src = URL.createObjectURL(file); objectURLCache.set(file, src); }

        // Store selected files on the element for payment handlers
        inputEl._selectedFiles = filesArr;
        // For type="file" inputs setting .value throws a SecurityError; skip it.
        // getInvitationFiles() reads _selectedFiles first so the value attribute is not needed.
        if (inputEl.type !== 'file') inputEl.value = src;

        // ── Update preview container ────────────────────────────────────
        if (previewEl) {
            if (mediaType === 'image') {
                previewEl.innerHTML =
                    '<div class="preview-content image-preview">' +
                        '<img src="' + src + '" alt="Preview" data-key-uid="' + esc(uid) + '" class="preview-image">' +
                    '</div>';
            } else if (mediaType === 'video') {
                previewEl.innerHTML =
                    '<div class="preview-content video-preview">' +
                        '<video src="' + src + '" data-key-uid="' + esc(uid) + '" class="preview-video" autoplay muted loop playsinline preload="auto"></video>' +
                    '</div>';
                // Start playback immediately (autoplay may be blocked without user gesture)
                var newVid = previewEl.querySelector('.preview-video');
                if (newVid) newVid.play().catch(function () {});
            } else if (mediaType === 'audio') {
                var info = typeInfo(file) || { fg: '#db2777', thumbBg: '#fce7f3' };
                previewEl.innerHTML =
                    '<div class="preview-content audio-preview" style="background:' + info.thumbBg + ';flex-direction:column;gap:8px">' +
                        '<i class="bi bi-music-note-beamed" style="font-size:2rem;color:' + info.fg + '"></i>' +
                        '<span style="font-size:0.75rem;color:#555;font-weight:600;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;padding:0 8px">' + esc(file._originalName || file.name) + '</span>' +
                    '</div>';
            }
        }

        // ── Show delete button ────────────────────────────────────────────
        // inputEl may live outside .media-preview-card (e.g. cover-image template),
        // so also try walking up from the sibling previewEl.
        var card = inputEl.closest('.media-preview-card')
                || (previewEl && previewEl.closest('.media-preview-card'));
        if (card) {
            var delBtn = card.querySelector('.media-delete-button');
            if (delBtn) delBtn.setAttribute('data-show-btn', 'true');

            // For video files: show per-card mute button (video plays muted by default)
            if (mediaType === 'video') {
                var muteBtn = card.querySelector('.media-mute-button');
                if (muteBtn) {
                    muteBtn.setAttribute('data-show-btn', 'true');
                    var muteIcon = muteBtn.querySelector('i');
                    if (muteIcon) muteIcon.className = 'bi bi-volume-mute-fill';
                }
            }
        }
        inputEl.setAttribute('data-file-action', 'upload');

        // Show global mute button for any media type that carries audio/video
        if (mediaType === 'video' || mediaType === 'audio') {
            var globalMusicBtn = document.getElementById('musicBtn');
            if (globalMusicBtn) globalMusicBtn.classList.remove('d-none');
        }

        // ── Update iframe preview ─────────────────────────────────────────────
        if (uid) {
            var elems = Array.from(document.querySelectorAll('[data-key-uid="' + uid + '"]'));
            elems.forEach(function (el) {
                if (el.tagName === 'IMG' || el.tagName === 'VIDEO') {
                    el.src = src;
                    if (el.tagName === 'VIDEO') { try { el.load(); } catch(e) {} }
                }
            });
            var iframe = document.getElementById('theme-iframe');
            var iDoc = iframe && (iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document));
            if (iDoc) {
                var iElems = Array.from(iDoc.querySelectorAll('[data-key-uid="' + uid + '"]'));
                iElems.forEach(function (el) {
                    if (el.tagName === 'IMG' || el.tagName === 'VIDEO') {
                        el.src = src;
                        if (el.tagName === 'VIDEO') { try { el.load(); } catch(e) {} }
                    }
                });
            }
        }

        // ── Special case: background music ───────────────────────────────────
        if (uid === 'background_music' && window.backgroundMusicData) {
            window.backgroundMusicData.file     = file;
            window.backgroundMusicData.fileName = file._originalName || file.name;
            window.backgroundMusicData.fileSize = fmtSize(file.size);
            window.backgroundMusicData.fileUrl  = src;
            if (typeof window.displayMusicPreview === 'function') window.displayMusicPreview();
            if (typeof window.showMusicControlButton === 'function') window.showMusicControlButton();
            if (typeof window.saveBackgroundMusicToStorage === 'function') window.saveBackgroundMusicToStorage();
            if (typeof window.updateIframeMusic === 'function') window.updateIframeMusic();
        }

        // ── Fire change event so any data-storage listeners pick it up ─────────────
        var ev = new Event('change', { bubbles: true });
        // Attach a synthetic files array so handlers that read .files still work
        Object.defineProperty(ev, 'target', { value: inputEl, writable: false });
        inputEl.dispatchEvent(ev);
    }

    // Public API
    window.applyMediaFileToInput = function (triggerEl, filesArr) {
        var t = resolveTargets(triggerEl);
        applyFileToInput(t.inputEl || triggerEl, t.previewEl, filesArr);
    };

    // Apply a saved server-file (URL) to a field input — marks it as 'replace'
    // so the server knows to keep the existing file (no re-upload needed unless
    // a new local file is also chosen later).
    function applyServerUrlToInput(inputEl, previewEl, sf) {
        if (!inputEl) return;
        var mt = sf.mediaType || 'image';
        var uid = inputEl.getAttribute('data-theme-uid') || '';

        // Update the preview card
        if (previewEl) {
            if (mt === 'image') {
                previewEl.innerHTML =
                    '<div class="preview-content image-preview">' +
                        '<img src="' + esc(sf.url) + '" alt="Preview" data-key-uid="' + esc(uid) + '" class="preview-image">' +
                    '</div>';
            } else if (mt === 'video') {
                previewEl.innerHTML =
                    '<div class="preview-content video-preview">' +
                        '<video src="' + esc(sf.url) + '" data-key-uid="' + esc(uid) + '" class="preview-video" autoplay muted loop playsinline preload="auto"></video>' +
                    '</div>';
                var newVid = previewEl.querySelector('.preview-video');
                if (newVid) newVid.play().catch(function () {});
            } else {
                var info = sf.info || { fg: '#db2777', thumbBg: '#fce7f3' };
                previewEl.innerHTML =
                    '<div class="preview-content audio-preview" style="background:' + info.thumbBg + ';flex-direction:column;gap:8px">' +
                        '<i class="bi bi-music-note-beamed" style="font-size:2rem;color:' + info.fg + '"></i>' +
                        '<span style="font-size:0.75rem;color:#555;font-weight:600;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;padding:0 8px">' + esc(sf.name) + '</span>' +
                    '</div>';
            }
        }
        // Show delete / mute buttons
        var card = inputEl.closest('.media-preview-card') || (previewEl && previewEl.closest('.media-preview-card'));
        if (card) {
            var delBtn = card.querySelector('.media-delete-button');
            if (delBtn) delBtn.setAttribute('data-show-btn', 'true');
            if (mt === 'video') {
                var muteBtn = card.querySelector('.media-mute-button');
                if (muteBtn) muteBtn.setAttribute('data-show-btn', 'true');
            }
        }
        // Keep the existing server file — mark as 'replace' so it won't be re-uploaded
        inputEl.setAttribute('data-file-action', 'replace');
        // type="file" inputs throw SecurityError on .value assignment; skip it.
        if (inputEl.type !== 'file') inputEl.value = sf.url;
        inputEl._selectedFiles = null; // no new local file

        // Re-add to server files list in case it was previously deleted
        var already = (window.mediaServerFiles || []).some(function (s) { return s.uid === sf.uid; });
        if (!already) { window.mediaServerFiles.push(sf); renderList(); }
    }

    // ── Picker sheet ────────────────────────────────────────────────────────────

    var pickerEl = null;

    function buildPickerDOM() {
        if (document.getElementById('mupPicker')) return;
        var el = document.createElement('div');
        el.id = 'mupPicker';
        el.className = 'mup-picker';
        el.innerHTML =
            '<div class="mup-picker-backdrop" id="mupPickerBackdrop"></div>' +
            '<div class="mup-picker-sheet">' +
                '<div class="mup-picker-header">' +
                    '<span class="mup-picker-title" id="mupPickerTitle">Select File</span>' +
                    '<button type="button" class="mup-picker-close" id="mupPickerClose"><i class="bi bi-x-lg"></i></button>' +
                '</div>' +
                '<div class="mup-picker-body" id="mupPickerBody"></div>' +
                '<div class="mup-picker-footer" id="mupPickerFooter">' +
                    '<input type="file" id="mupPickerFileInput" style="display:none">' +
                    '<button type="button" class="mup-picker-upload-btn" id="mupPickerUploadBtn">' +
                        '<i class="bi bi-upload mup-upload-icon"></i>' +
                        '<span class="mup-upload-spinner" aria-hidden="true"></span>' +
                        ' Upload new file' +
                    '</button>' +
                    '<button type="button" class="mup-picker-confirm" id="mupPickerConfirm" style="display:none">Use Selected</button>' +
                '</div>' +
            '</div>';
        document.body.appendChild(el);
        pickerEl = el;
    }

    function closePicker() {
        if (!pickerEl) return;
        pickerEl.classList.remove('mup-picker--open');
    }

    function openPickerSheet(filtered, mediaType, isMultiple, onConfirm) {
        buildPickerDOM();
        pickerEl = document.getElementById('mupPicker');
        var body       = document.getElementById('mupPickerBody');
        var footer     = document.getElementById('mupPickerFooter');
        var title      = document.getElementById('mupPickerTitle');
        var confirm    = document.getElementById('mupPickerConfirm');
        var close      = document.getElementById('mupPickerClose');
        var backdrop   = document.getElementById('mupPickerBackdrop');
        var uploadBtn  = document.getElementById('mupPickerUploadBtn');
        var fileInp    = document.getElementById('mupPickerFileInput');

        // Label
        var typeLabel = mediaType === 'audio' ? 'Audio' : mediaType === 'video' ? 'Video' : 'Image';
        title.textContent = 'Select ' + typeLabel;

        // Configure the hidden file input for this picker session
        var acceptMap = { image: 'image/*', video: 'video/*', audio: 'audio/*' };
        fileInp.accept   = acceptMap[mediaType] || '*/*';
        fileInp.multiple = isMultiple;
        fileInp.value    = ''; // reset so same file can be re-selected

        // "Use Selected" only shown in multi-select mode
        confirm.style.display = isMultiple ? '' : 'none';

        var selected = [];

        function updateConfirm() {
            confirm.textContent = 'Use Selected (' + selected.length + ')';
            confirm.disabled = selected.length === 0;
        }

        // ── Body content ───────────────────────────────────────────────────────────
        function renderBody(list) {
            body.innerHTML = '';

            // Server files of the same type that are still present (not deleted)
            var serverMatches = (window.mediaServerFiles || []).filter(function (sf) {
                return sf.mediaType === mediaType;
            });

            if (!list.length && !serverMatches.length) {
                body.innerHTML =
                    '<div class="mup-picker-empty">' +
                        '<i class="bi bi-' + (mediaType === 'audio' ? 'music-note-beamed' : mediaType === 'video' ? 'play-circle' : 'image') + '"></i>' +
                        '<p>No ' + typeLabel.toLowerCase() + ' files in your media library yet.</p>' +
                        '<small>Upload a file using the button below.</small>' +
                    '</div>';
                return;
            }

            var grid = document.createElement('div');
            grid.className = 'mup-picker-grid';

            // ── Server files first ─────────────────────────────────────────────────
            serverMatches.forEach(function (sf) {
                var info = sf.info || { icon: 'bi-file-earmark', fg: '#888', thumbBg: '#f0f0f0', pillBg: '#f8f8f8', label: 'File' };
                var mt2  = sf.mediaType || 'image';

                var previewHTML;
                if (mt2 === 'image') {
                    previewHTML = '<img class="mup-card-img" src="' + esc(sf.url) + '" alt="">';
                } else if (mt2 === 'video') {
                    previewHTML =
                        '<video class="mup-card-video" src="' + esc(sf.url) + '" muted playsinline preload="metadata"></video>' +
                        '<span class="mup-card-play-icon"><i class="bi bi-play-circle-fill"></i></span>';
                } else {
                    previewHTML =
                        '<span class="mup-card-icon-fallback" style="color:' + info.fg + '"><i class="bi ' + info.icon + '"></i></span>';
                }

                var card = document.createElement('div');
                card.className = 'mup-card mup-picker-card mup-picker-card--server';
                card.innerHTML =
                    '<div class="mup-card-visual" style="background:' + info.thumbBg + '">' +
                        previewHTML +
                        '<span class="mup-card-badge" style="background:#dcfce7;color:#166534">Saved</span>' +
                        '<span class="mup-picker-check"><i class="bi bi-check-lg"></i></span>' +
                    '</div>';

                card.addEventListener('click', function () {
                    // Apply server URL directly to the input (no File object)
                    closePicker();
                    applyServerUrlToInput(onConfirm._inputEl, onConfirm._previewEl, sf);
                });

                grid.appendChild(card);
            });

            // ── New (local) files ──────────────────────────────────────────────────
            list.forEach(function (file) {
                var info = typeInfo(file) || { icon: 'bi-file-earmark', fg: '#888', thumbBg: '#f0f0f0', pillBg: '#f8f8f8', label: 'File' };
                var isImg = file.type.startsWith('image/');
                var isVid = file.type.startsWith('video/');

                var src = objectURLCache.get(file);
                if (!src) { src = URL.createObjectURL(file); objectURLCache.set(file, src); }

                var previewHTML;
                if (isImg) {
                    previewHTML = '<img class="mup-card-img" src="' + src + '" alt="">';
                } else if (isVid) {
                    previewHTML = '<video class="mup-card-video" src="' + src + '" muted playsinline preload="metadata"></video>' +
                                  '<span class="mup-card-play-icon"><i class="bi bi-play-circle-fill"></i></span>';
                } else {
                    previewHTML = '<span class="mup-card-icon-fallback" style="color:' + info.fg + '"><i class="bi ' + info.icon + '"></i></span>';
                }

                var card = document.createElement('div');
                card.className = 'mup-card mup-picker-card';
                card.innerHTML =
                    '<div class="mup-card-visual" style="background:' + info.thumbBg + '">' +
                        previewHTML +
                        '<span class="mup-card-badge" style="background:' + info.pillBg + ';color:' + info.fg + '">' + info.label + '</span>' +
                        '<button type="button" class="mup-card-remove mup-picker-card-remove" title="Remove from library"><i class="bi bi-x"></i></button>' +
                        '<span class="mup-picker-check"><i class="bi bi-check-lg"></i></span>' +
                    '</div>';

                // Remove-from-library button
                card.querySelector('.mup-picker-card-remove').addEventListener('click', function (e) {
                    e.stopPropagation();
                    var globalIdx = window.mediaUploadFiles.indexOf(file);
                    if (globalIdx !== -1) removeFile(globalIdx);
                    var newFiltered = (window.mediaUploadFiles || []).filter(function (f) {
                        return f.type.startsWith(mediaType + '/');
                    });
                    // Also remove from selected if present
                    var selIdx = selected.indexOf(file);
                    if (selIdx !== -1) { selected.splice(selIdx, 1); updateConfirm(); }
                    renderBody(newFiltered);
                });

                card.addEventListener('click', function (e) {
                    if (e.target.closest('.mup-picker-card-remove')) return;
                    if (isMultiple) {
                        var idx = selected.indexOf(file);
                        if (idx === -1) { selected.push(file); card.classList.add('mup-picker-card--selected'); }
                        else           { selected.splice(idx, 1); card.classList.remove('mup-picker-card--selected'); }
                        updateConfirm();
                    } else {
                        closePicker();
                        onConfirm([file]);
                    }
                });

                grid.appendChild(card);
            });

            body.appendChild(grid);
        }

        renderBody(filtered);

        // ── Upload new file handler ────────────────────────────────────────────────
        var newUploadBtn = uploadBtn.cloneNode(true);
        uploadBtn.parentNode.replaceChild(newUploadBtn, uploadBtn);
        var newFileInp = fileInp.cloneNode(true);
        fileInp.parentNode.replaceChild(newFileInp, fileInp);
        newFileInp.accept   = acceptMap[mediaType] || '*/*';
        newFileInp.multiple = isMultiple;

        newUploadBtn.addEventListener('click', function (e) {
            e.stopPropagation();
            // Show loader while file dialog is open
            var uploadIcon = newUploadBtn.querySelector('.mup-upload-icon');
            var uploadSpin = newUploadBtn.querySelector('.mup-upload-spinner');
            if (uploadIcon) uploadIcon.style.display = 'none';
            if (uploadSpin) uploadSpin.style.display = 'inline-block';
            newUploadBtn.disabled = true;
            newFileInp.click();
            // Restore button when dialog dismisses without a selection (window refocus)
            function restoreUploadBtn() {
                if (uploadIcon) uploadIcon.style.display = '';
                if (uploadSpin) uploadSpin.style.display = 'none';
                newUploadBtn.disabled = false;
            }
            setTimeout(function () {
                window.addEventListener('focus', function onFocus() {
                    setTimeout(restoreUploadBtn, 300);
                    window.removeEventListener('focus', onFocus);
                });
            }, 400);
            newFileInp._restoreUploadBtn = restoreUploadBtn;
        });

        newFileInp.addEventListener('change', function () {
            // Restore upload button immediately on file selection
            if (newFileInp._restoreUploadBtn) { newFileInp._restoreUploadBtn(); }
            if (!newFileInp.files || !newFileInp.files.length) return;
            var errors = [];
            var toApply = [];
            Array.from(newFileInp.files).forEach(function (file) {
                var info = typeInfo(file);
                if (!info) { errors.push('"' + file.name + '" — unsupported type.'); return; }
                if (file.size > MAX_BYTES) { errors.push('"' + file.name + '" exceeds 10 MB.'); return; }
                var exists = window.mediaUploadFiles.some(function (f) {
                    return f._originalName === file.name && f._originalSize === file.size;
                });
                if (!exists) {
                    var renamed = new File([file], randomName(file), { type: file.type });
                    renamed._originalName = file.name;
                    renamed._originalSize = file.size;
                    window.mediaUploadFiles.push(renamed);
                    toApply.push(renamed);
                } else {
                    // Already in library — find it and add to apply list
                    var existing = window.mediaUploadFiles.find(function (f) {
                        return f._originalName === file.name && f._originalSize === file.size;
                    });
                    if (existing) toApply.push(existing);
                }
            });

            newFileInp.value = '';
            if (errors.length) showToast(errors);

            if (toApply.length) {
                if (isMultiple) {
                    // Refresh the picker grid so newly added cards appear
                    var updatedFiltered = (window.mediaUploadFiles || []).filter(function (f) {
                        return f.type.startsWith(mediaType + '/');
                    });
                    renderBody(updatedFiltered);
                    // Sync the media tab grid + header count
                    if (fileList) renderList();
                    if (headerCount) {
                        headerCount.textContent = window.mediaUploadFiles.length;
                        headerCount.style.display = '';
                    }
                } else {
                    // Single: close and apply immediately
                    closePicker();
                    onConfirm(toApply);
                    // Refresh the media tab grid in background
                    if (fileList) renderList();
                }
            }
        });

        // ── Confirm (multiple) ─────────────────────────────────────────────────────────
        var newConfirm = confirm.cloneNode(true);
        confirm.parentNode.replaceChild(newConfirm, confirm);
        updateConfirm();
        newConfirm.addEventListener('click', function () {
            if (!selected.length) return;
            closePicker();
            onConfirm(selected);
        });

        // ── Close handlers ──────────────────────────────────────────────────────────────
        var newClose = close.cloneNode(true);
        close.parentNode.replaceChild(newClose, close);
        newClose.addEventListener('click', closePicker);

        var newBackdrop = backdrop.cloneNode(true);
        backdrop.parentNode.replaceChild(newBackdrop, backdrop);
        newBackdrop.addEventListener('click', closePicker);

        pickerEl.classList.add('mup-picker--open');
    }

    // Public API
    window.openMediaPicker = function (inputEl, previewEl) {
        // If caller passed a non-input element (e.g. the div with same id), try to resolve:
        if (inputEl && (!inputEl.getAttribute || !inputEl.getAttribute('data-media-type'))) {
            var t = resolveTargets(inputEl);
            // Only override if resolution worked
            if (t.inputEl) { inputEl = t.inputEl; }
            if (!previewEl && t.previewEl) { previewEl = t.previewEl; }
        }
        if (!inputEl || !inputEl.getAttribute) return;

        // Ensure server files are loaded even if the Media tab was never opened
        loadServerFiles();

        var mediaType  = (inputEl.getAttribute('data-media-type') || 'image').toLowerCase();
        var isMultiple = inputEl.getAttribute('data-file-multiple') === 'true';

        // Filter library by type
        var filtered = (window.mediaUploadFiles || []).filter(function (f) {
            return f.type.startsWith(mediaType + '/');
        });

        var confirmCb = function (filesArr) {
            applyFileToInput(inputEl, previewEl, filesArr);
        };
        // Attach refs so server-file cards in the picker can call applyServerUrlToInput
        confirmCb._inputEl   = inputEl;
        confirmCb._previewEl = previewEl;

        openPickerSheet(filtered, mediaType, isMultiple, confirmCb);
    };

    // ── Init ──────────────────────────────────────────────────────────────────

    function init() {
        if (initialized) {
            // Tab re-opened: just re-render in case files changed
            renderList();
            return;
        }

        dropzone    = document.getElementById('mupDropzone');
        fileInput   = document.getElementById('mupFileInput');
        fileList    = document.getElementById('mupFileList');
        headerCount = document.getElementById('mupHeaderCount');

        if (!dropzone || !fileInput) {
            console.warn('[media-upload] DOM elements not found.');
            return;
        }

        initialized = true;

        // Load any already-saved server files (update-invite page)
        loadServerFiles();

        // Click drop-zone → open picker
        dropzone.addEventListener('click', function (e) {
            if (e.target.closest('.mup-remove-btn')) return;
            fileInput.click();
        });

        // File picker change
        fileInput.addEventListener('change', function () {
            if (fileInput.files && fileInput.files.length) addFiles(fileInput.files);
            fileInput.value = ''; // Reset so same file can be re-added after removal
        });

        // Drag & drop
        ['dragenter', 'dragover'].forEach(function (evt) {
            dropzone.addEventListener(evt, function (e) {
                e.preventDefault();
                dropzone.classList.add('mup-dropzone--over');
            });
        });
        ['dragleave', 'drop'].forEach(function (evt) {
            dropzone.addEventListener(evt, function (e) {
                e.preventDefault();
                dropzone.classList.remove('mup-dropzone--over');
            });
        });
        dropzone.addEventListener('drop', function (e) {
            if (e.dataTransfer && e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
        });

        renderList();
    }

    // Re-load server files whenever the stored data changes (e.g. after background init)
    window.addEventListener('formDataSaved', function () {
        loadServerFiles(); // always refresh, even if media tab was never opened
        if (initialized) renderList();
    });

    // Expose so other modules (e.g. gift-registry) can ensure server files are
    // loaded before opening the picker without requiring the media tab to be visited.
    window.ensureMediaServerFilesLoaded = loadServerFiles;

    // ── PPM Files panel init ──────────────────────────────────────────────────
    // Called from popup-model.js whenever the ppm-files tab is switched into view.

    function initPPMFiles() {
        var ppmDz  = document.getElementById('ppmMupDropzone');
        var ppmInp = document.getElementById('ppmMupFileInput');
        var ppmEl  = document.getElementById('ppmMupFileList');

        if (!ppmDz || !ppmInp || !ppmEl) return;

        if (!ppmFilesInitialized) {
            ppmFilesInitialized = true;

            // Click dropzone → open file picker
            ppmDz.addEventListener('click', function (e) {
                if (e.target.closest('.mup-remove-btn')) return;
                ppmInp.click();
            });

            // File input change
            ppmInp.addEventListener('change', function () {
                if (ppmInp.files && ppmInp.files.length) addFiles(ppmInp.files);
                ppmInp.value = '';
            });

            // Drag & drop
            ['dragenter', 'dragover'].forEach(function (evt) {
                ppmDz.addEventListener(evt, function (e) {
                    e.preventDefault();
                    ppmDz.classList.add('mup-dropzone--over');
                });
            });
            ['dragleave', 'drop'].forEach(function (evt) {
                ppmDz.addEventListener(evt, function (e) {
                    e.preventDefault();
                    ppmDz.classList.remove('mup-dropzone--over');
                });
            });
            ppmDz.addEventListener('drop', function (e) {
                if (e.dataTransfer && e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
            });

            // Ensure server files are loaded on first open
            loadServerFiles();
        }

        // Re-render every time the tab is opened so the list is current
        renderListTo(ppmEl, null);
    }

    window.initPPMFiles = initPPMFiles;

    // ── Watch for tab activation ───────────────────────────────────────────────

    function watchTab() {
        const tab = document.querySelector('[data-model="media-tab"]');
        if (!tab) return;

        const obs = new MutationObserver(function () {
            if (tab.getAttribute('data-state') === 'show') init();
        });
        obs.observe(tab, { attributes: true, attributeFilter: ['data-state'] });

        // In case it's already visible on load
        if (tab.getAttribute('data-state') === 'show') init();
    }

    function eagerLoadServerFiles() {
        loadServerFiles();
        watchTab();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', eagerLoadServerFiles);
    } else {
        eagerLoadServerFiles();
    }
})();
